import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { admin, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (admin) navigate("/admin"); }, [admin, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password.trim());
      navigate("/admin");
    } catch (err) {
      if (!err.response) {
        setError("Couldn't reach the server. Check your internet connection (or reopen the live site if you were on a static preview) and try again.");
      } else if (err.response.status === 401) {
        setError("Invalid email or password. Tip: it's case-sensitive — watch for an accidental capital letter or a space.");
      } else {
        setError(formatApiError(err.response?.data?.detail) || "Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-sm bg-input border border-gold/20 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-gold";

  return (
    <div className="min-h-screen grid place-items-center bg-navy px-5 relative grain">
      <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        onSubmit={submit} className="relative z-10 w-full max-w-md rounded-sm bg-navy-light border border-gold/30 p-9" data-testid="admin-login-form">
        <div className="flex items-center gap-3 mb-8">
          <img src="/logo.png?v=2" alt="Bridge Taxi" className="h-14 w-14 rounded-full object-cover ring-1 ring-gold/40" />
          <div>
            <div className="font-heading text-lg font-bold text-white">Bridge Taxi</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold">Admin Portal</div>
          </div>
        </div>
        <h1 className="font-heading text-2xl font-bold text-white flex items-center gap-2 mb-1"><Lock className="h-5 w-5 text-gold" /> Sign in</h1>
        <p className="text-slate-400 text-sm mb-6">Access the bookings dashboard.</p>
        {error && <div className="mb-4 rounded-sm bg-destructive/20 border border-destructive/40 px-4 py-2.5 text-sm text-red-200" data-testid="login-error">{error}</div>}
        <div className="space-y-4">
          <input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="Email" className={inputCls}
            autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email" />
          <div className="relative">
            <input data-testid="admin-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Password" className={`${inputCls} pr-12`}
              autoComplete="current-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
            <button type="button" onClick={() => setShowPw((s) => !s)} data-testid="toggle-password"
              aria-label={showPw ? "Hide password" : "Show password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-gold transition-colors">
              {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
        </div>
        <button type="submit" disabled={loading} data-testid="admin-login-btn"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 font-semibold text-navy transition-transform duration-200 hover:-translate-y-0.5 hover:bg-gold-light disabled:opacity-60">
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</> : "Sign in"}
        </button>
      </motion.form>
    </div>
  );
}
